import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PhoneOutlined,
  PlayCircleFilled,
  TeamOutlined,
} from "@ant-design/icons";
import { Collapse } from "antd";
import { type ReactNode, useState } from "react";
import { useIsMobileView } from "../../../hooks/ui-hook";
import styles from "./CallDashboardLandingPage.module.scss";

const { Panel } = Collapse;

// Existing booking flow reused from the previous Front-Desk landing page.
const CALENDLY_URL = "https://calendly.com/nudge-therapy/schedule-demo-nudge-ai";

// Product walkthrough (unchanged id from the previous page / design handoff).
const DEMO_VIDEO_ID = "BP1IJcnScyQ";
const VIDEO_SECTION_ID = "frontdesk-landing-video";

type Stat = { value: string; label: string };
const HERO_STATS: Stat[] = [
  { value: "35%", label: "more new-patient bookings" },
  { value: "80%", label: "fewer missed calls" },
  { value: "70%", label: "of calls handled without staff" },
  { value: "24/7", label: "after-hours & weekends" },
];

type FaqItem = { question: string; answer: string };
const FAQ_DATA: FaqItem[] = [
  {
    question: "Can I customize how it sounds and what it says?",
    answer:
      "Absolutely. We'll build it with your chosen voice, your practice preferences, and your workflows. Every call sounds exactly like your team would handle it. Choose from 10+ voices and we configure everything to match your brand.",
  },
  {
    question: "How quickly can this be live?",
    answer:
      "Book your implementation session and we'll have your AI front desk custom-built and answering calls within 48 hours. No IT team needed, no complex setup. We handle everything.",
  },
  {
    question: "What does this actually cost?",
    answer:
      "Plans start at $400/month for 500 minutes of call time each month (approximately 250 calls). Higher-volume practices get custom pricing. That's less than a quarter of what you'd pay for a part-time receptionist — for 24/7 coverage.",
  },
  {
    question: "What happens when the AI can't handle a call?",
    answer:
      "We redirect the call directly to your team with full context from the conversation. Your staff gets a summary of what was discussed, so they can pick up right where the AI left off.",
  },
  {
    question: "How do I know what's happening with calls?",
    answer:
      "Your calls dashboard shows every call with AI-generated summaries, actionable next steps, patient info collected, smart tags for each call type, and full transcripts you can review anytime. See exactly what patients are asking for and spot trends in real time.",
  },
];

// ── Calls Dashboard mock (hero product visual) ──────────────────────────────
// Faithful, compact replica of the real Calls Dashboard (call-dashboard.component).
type DashStat = { icon: ReactNode; value: string; label: string };
const DASH_STATS: DashStat[] = [
  { icon: <PhoneOutlined />, value: "90", label: "Total Calls" },
  { icon: <ClockCircleOutlined />, value: "3", label: "Urgent" },
  { icon: <TeamOutlined />, value: "24", label: "New Patients" },
  { icon: <TeamOutlined />, value: "63", label: "Existing" },
];

type CallTag = { label: string; urgent?: boolean };
type SampleCall = { name: string; tags: CallTag[]; time: string; phone: string; summary: string };
const SAMPLE_CALLS: SampleCall[] = [
  {
    name: "Sarah Mitchell",
    tags: [{ label: "New Patient" }],
    time: "Today, 2:14 PM",
    phone: "(415) 555-0142",
    summary: "New patient requesting a first appointment for anxiety. Booked Tue 10:00 AM with Dr. Lee.",
  },
  {
    name: "James Carter",
    tags: [{ label: "Urgent", urgent: true }, { label: "Prescription" }],
    time: "Today, 1:02 PM",
    phone: "(415) 555-0198",
    summary: "Existing patient out of sertraline. Flagged urgent and routed to the front desk for a same-day refill.",
  },
  {
    name: "Maria Gomez",
    tags: [{ label: "Scheduling" }],
    time: "Today, 11:40 AM",
    phone: "(628) 555-0177",
    summary: "Rescheduled Thursday's session to next Monday at 4:00 PM and confirmed the new time by text.",
  },
];

const SkillTag = ({ label, urgent }: CallTag) => (
  <span className={`${styles.landingPage__skill} ${urgent ? styles.landingPage__skill_urgent : ""}`}>{label}</span>
);

const CallsInboxMock = () => (
  <div className={styles.landingPage__mock_card}>
    <div className={styles.landingPage__mock_header}>
      <div>
        <div className={styles.landingPage__mock_title}>Calls Dashboard</div>
        <div className={styles.landingPage__mock_subtitle}>Manage patient calls and inquiries</div>
      </div>
      <span className={styles.landingPage__mock_live}>
        <span className={styles.landingPage__mock_live_dot} />
        Live
      </span>
    </div>

    <div className={styles.landingPage__mock_stats}>
      {DASH_STATS.map((stat) => (
        <div className={styles.landingPage__mock_stat} key={stat.label}>
          <div className={styles.landingPage__mock_stat_top}>
            <span className={styles.landingPage__mock_stat_icon}>{stat.icon}</span>
            <span className={styles.landingPage__mock_stat_value}>{stat.value}</span>
          </div>
          <div className={styles.landingPage__mock_stat_label}>{stat.label}</div>
        </div>
      ))}
    </div>

    <div className={styles.landingPage__mock_history}>
      <span className={styles.landingPage__mock_history_label}>Call History</span>
      <span className={styles.landingPage__mock_history_chip}>
        <CheckOutlined />
        Today
      </span>
    </div>

    <div className={styles.landingPage__mock_calls}>
      <div className={styles.landingPage__mock_scan} aria-hidden="true" />
      {SAMPLE_CALLS.map((call) => (
        <div className={styles.landingPage__mock_call} key={call.phone}>
          <div className={styles.landingPage__mock_call_top}>
            <div className={styles.landingPage__mock_call_who}>
              <span className={styles.landingPage__mock_call_name}>{call.name}</span>
              {call.tags.map((tag) => (
                <SkillTag key={tag.label} label={tag.label} urgent={tag.urgent} />
              ))}
            </div>
            <span className={styles.landingPage__mock_call_time}>
              <CalendarOutlined />
              {call.time}
            </span>
          </div>
          <div className={styles.landingPage__mock_call_phone}>
            <PhoneOutlined />
            <span>{call.phone}</span>
          </div>
          <div className={styles.landingPage__mock_call_summary}>
            <span className={styles.landingPage__mock_call_summary_label}>Call Summary</span> : {call.summary}
          </div>
          <span className={styles.landingPage__mock_call_transcript}>
            <FileTextOutlined />
            View transcript
          </span>
        </div>
      ))}
    </div>
  </div>
);

const CallDashboardLandingPage = () => {
  const isMobile = useIsMobileView();
  const [videoPlaying, setVideoPlaying] = useState(false);

  const handleBookASession = () => {
    window.open(CALENDLY_URL, "_blank");
  };

  const scrollToVideo = () => {
    // The page lives inside Ant Design's <Content> (overflow:auto), not the
    // document — window.scrollTo would no-op. scrollIntoView walks up to the
    // actual scroll ancestor and scrolls it.
    document.getElementById(VIDEO_SECTION_ID)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className={styles.landingPage}>
      {/* ── Hero (warm) ── */}
      <div className={styles.landingPage__hero_band}>
        <div className={styles.landingPage__container}>
          <section className={styles.landingPage__hero}>
            <div className={styles.landingPage__hero_grid}>
              <div className={styles.landingPage__hero_copy}>
                <span className={styles.landingPage__hero_pill}>
                  <PhoneOutlined />
                  24/7 AI Front-Desk
                </span>
                <h1 className={styles.landingPage__hero_title}>
                  It handles the calls.
                  <br />
                  <span className={styles.landingPage__hero_title_accent}>You handle the care.</span>
                </h1>
                <p className={styles.landingPage__hero_subcopy}>
                  Nudge's AI front desk answers every call in your practice's voice — booking appointments, handling
                  intake, and answering questions, day or night. Warm, patient, and always available.
                </p>
                <div className={styles.landingPage__hero_actions}>
                  <button type="button" className={styles.landingPage__btn_primary} onClick={handleBookASession}>
                    Book a demo
                    <ArrowRightOutlined />
                  </button>
                  <button type="button" className={styles.landingPage__btn_ghost} onClick={scrollToVideo}>
                    <PlayCircleFilled />
                    Watch the demo
                  </button>
                </div>
              </div>
              {!isMobile && (
                <div className={styles.landingPage__hero_mock}>
                  <CallsInboxMock />
                </div>
              )}
            </div>
          </section>

          <div className={styles.landingPage__statcards}>
            {HERO_STATS.map((stat) => (
              <div className={styles.landingPage__statcard} key={stat.label}>
                <div className={styles.landingPage__statcard_value}>{stat.value}</div>
                <div className={styles.landingPage__statcard_label}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Video ── */}
      <section id={VIDEO_SECTION_ID} className={styles.landingPage__video_section}>
        <div className={styles.landingPage__container}>
          <div className={styles.landingPage__section_head}>
            <div className={styles.landingPage__eyebrow}>See it in action</div>
            <h2 className={styles.landingPage__section_title}>Hear it handle a real call</h2>
            <p className={styles.landingPage__section_sub}>
              Two minutes on how Nudge answers, books, and routes — so no patient hits a voicemail.
            </p>
          </div>

          <div className={styles.landingPage__video_card}>
            {videoPlaying ? (
              <iframe
                src={`https://www.youtube.com/embed/${DEMO_VIDEO_ID}?autoplay=1&rel=0`}
                title="Nudge AI Front-Desk walkthrough"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className={styles.landingPage__video_iframe}
              />
            ) : (
              <button
                type="button"
                aria-label="Play walkthrough video"
                className={styles.landingPage__video_facade}
                style={{ backgroundImage: `url(https://i.ytimg.com/vi/${DEMO_VIDEO_ID}/maxresdefault.jpg)` }}
                onClick={() => setVideoPlaying(true)}
              >
                <span className={styles.landingPage__video_scrim} />
                <span className={styles.landingPage__video_play}>
                  <PlayCircleFilled />
                </span>
              </button>
            )}
          </div>

          <div className={styles.landingPage__cta_center}>
            <button type="button" className={styles.landingPage__btn_primary} onClick={handleBookASession}>
              Book a demo
              <ArrowRightOutlined />
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className={styles.landingPage__faq}>
        <div className={styles.landingPage__container}>
          <div className={styles.landingPage__section_head}>
            <div className={styles.landingPage__eyebrow}>FAQs</div>
            <h2 className={styles.landingPage__section_title}>Curious? Learn more.</h2>
          </div>

          <div className={styles.landingPage__faq_content}>
            <Collapse
              accordion
              ghost
              defaultActiveKey={["0"]}
              expandIconPosition="end"
              expandIcon={({ isActive }) => (
                <span
                  className={styles.landingPage__faq_plus}
                  style={{ transform: isActive ? "rotate(45deg)" : "none" }}
                >
                  +
                </span>
              )}
              className={styles.landingPage__faq_collapse}
            >
              {FAQ_DATA.map((faq, i) => (
                <Panel
                  header={<span className={styles.landingPage__faq_question}>{faq.question}</span>}
                  key={String(i)}
                  className={styles.landingPage__faq_panel}
                >
                  <p className={styles.landingPage__faq_answer}>{faq.answer}</p>
                </Panel>
              ))}
            </Collapse>

            <div className={styles.landingPage__cta_center}>
              <button type="button" className={styles.landingPage__btn_primary} onClick={handleBookASession}>
                Book a demo
                <ArrowRightOutlined />
              </button>
              <p className={styles.landingPage__cta_finecopy}>
                Live in 48 hours · Less than a quarter the cost of a part-time receptionist.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CallDashboardLandingPage;
